/**
 * Energy analysis engine for the Analyze tab.
 *
 * Implements a simplified but deterministic transfer-function style load
 * model per thermal Space (roughly the ASHRAE Blocks / "simple envelope"
 * method BIM's Legacy Energy Analysis used before Insight), plus an
 * annual energy estimate with utility-rate and carbon-intensity presets
 * (the numbers the Insight-style summary panel shows), and a gbXML 6.0
 * document serializer for the Create Energy Model output.
 *
 * Units: geometry in meters (the BIM kernel's world units), temperatures
 * in °C, areas m², volumes m³, loads W, energy kWh, cost currency units.
 */

import { v4 as uuidv4 } from 'uuid';
import { BIMElement } from '../../core/types';

// ==================== Settings ====================

export interface EnergySettings {
    buildingType: BuildingType;
    heatingSetpointC: number;
    coolingSetpointC: number;
    /** Winter design dry-bulb used by the load calculation. */
    winterDesignTempC: number;
    /** Summer design dry-bulb used by the load calculation. */
    summerDesignTempC: number;
    /** Summer design wet-bulb (drives latent cooling allowance). */
    summerDesignWetBulbC: number;
    /** Utility rate, currency per kWh. */
    electricityRate: number;
    /** Natural gas rate, currency per kWh HH. */
    gasRate: number;
    /** Grid carbon intensity, kgCO2e per kWh. */
    carbonIntensity: number;
}

export type BuildingType =
    | 'Office'
    | 'Residential'
    | 'School'
    | 'Retail'
    | 'Hospital'
    | 'Warehouse'
    | 'Data Center'
    | 'Other';

export const BUILDING_TYPES: BuildingType[] = [
    'Office',
    'Residential',
    'School',
    'Retail',
    'Hospital',
    'Warehouse',
    'Data Center',
    'Other',
];

export const DEFAULT_ENERGY_SETTINGS: EnergySettings = {
    buildingType: 'Office',
    heatingSetpointC: 21,
    coolingSetpointC: 24,
    winterDesignTempC: -7,
    summerDesignTempC: 32,
    summerDesignWetBulbC: 23,
    electricityRate: 0.12,
    gasRate: 0.045,
    carbonIntensity: 0.4,
};

/** Climate presets wired into the Location-style defaults. */
export const CLIMATE_PRESETS: {
    name: string;
    winterDesignTempC: number;
    summerDesignTempC: number;
    summerDesignWetBulbC: number;
}[] = [
    { name: 'Severe Cold', winterDesignTempC: -18, summerDesignTempC: 28, summerDesignWetBulbC: 19 },
    { name: 'Cold', winterDesignTempC: -7, summerDesignTempC: 31, summerDesignWetBulbC: 21 },
    { name: 'Temperate', winterDesignTempC: -1, summerDesignTempC: 34, summerDesignWetBulbC: 23 },
    { name: 'Marine', winterDesignTempC: 2, summerDesignTempC: 27, summerDesignWetBulbC: 19 },
    { name: 'Hot-Dry', winterDesignTempC: 1, summerDesignTempC: 42, summerDesignWetBulbC: 16 },
    { name: 'Hot-Humid', winterDesignTempC: 4, summerDesignTempC: 35, summerDesignWetBulbC: 27 },
];

/** Load density presets (W/m² and people/m²) per ASHRAE-style block factors. */
const BUILDING_FACTORS: Record<
    BuildingType,
    { lightingWm2: number; equipmentWm2: number; peoplePerM2: number; sensiblePersonW: number; latentPersonW: number; infiltrationACH: number }
> = {
    Office: { lightingWm2: 12, equipmentWm2: 10, peoplePerM2: 0.08, sensiblePersonW: 75, latentPersonW: 55, infiltrationACH: 0.35 },
    Residential: { lightingWm2: 6, equipmentWm2: 4, peoplePerM2: 0.04, sensiblePersonW: 70, latentPersonW: 55, infiltrationACH: 0.5 },
    School: { lightingWm2: 11, equipmentWm2: 6, peoplePerM2: 0.25, sensiblePersonW: 75, latentPersonW: 60, infiltrationACH: 0.5 },
    Retail: { lightingWm2: 14, equipmentWm2: 5, peoplePerM2: 0.15, sensiblePersonW: 75, latentPersonW: 60, infiltrationACH: 0.5 },
    Hospital: { lightingWm2: 13, equipmentWm2: 15, peoplePerM2: 0.06, sensiblePersonW: 75, latentPersonW: 55, infiltrationACH: 0.6 },
    Warehouse: { lightingWm2: 5, equipmentWm2: 2, peoplePerM2: 0.01, sensiblePersonW: 100, latentPersonW: 60, infiltrationACH: 0.5 },
    'Data Center': { lightingWm2: 8, equipmentWm2: 400, peoplePerM2: 0.01, sensiblePersonW: 75, latentPersonW: 55, infiltrationACH: 0.3 },
    Other: { lightingWm2: 10, equipmentWm2: 8, peoplePerM2: 0.05, sensiblePersonW: 75, latentPersonW: 55, infiltrationACH: 0.4 },
};

// ==================== Types for model building ====================

export interface EnergySurfaceSpec {
    id: string;
    /** Space name this surface bounds (empty = exterior shell piece). */
    spaceName: string;
    category: 'Roof' | 'Floor' | 'Wall' | 'Glazing' | 'SlabOnGrade' | 'InteriorWall';
    area: number;      // m²
    uValue: number;    // W/m²K
    solarOrientation?: number; // degrees from north for glazing
}

export interface ThermalSpaceSpec {
    id: string;
    name: string;
    volume: number;   // m³
    floorArea: number; // m²
    exteriorWallArea: number;
    glazingArea: number;
    roofArea: number;
    slabArea: number;
    wallU: number;
    glazingU: number;
    shgc: number;
    roofU: number;
    slabF: number; // exposed slab F-factor edge loss, W/mK
}

export interface EnergyModelResult {
    spaces: ThermalSpaceSpec[];
    surfaces: EnergySurfaceSpec[];
    totalArea: number;
    totalVolume: number;
    createdOn: number;
}

// ==================== Default construction assemblies ====================

export const DEFAULT_ASSEMBLIES = {
    wallU: 0.35,          // W/m²K insulated cavity wall
    glazingU: 1.6,        // double glazing
    shgc: 0.4,
    roofU: 0.22,
    slabF: 0.73,          // F-factor for slab-on-grade perimeter
    floorU: 0.45,
};

// ==================== Model builder ====================

/**
 * Build the thermal energy model from the physical BIM model:
 * - Each 'Spaces' element (or 'Rooms' fallback) becomes a Thermal Space.
 * - Exterior 'Walls', 'Floors' (slab-on-grade when base ~ 0) and 'Roofs'
 *   are apportioned as shell surfaces around the space union.
 * The heuristic mirrors what BIM's Create Energy Model does at coarse
 * level: spaces union → exterior faces → construction assignments.
 */
export function buildEnergyModelFromElements(elements: BIMElement[]): EnergyModelResult {
    const spaces: ThermalSpaceSpec[] = [];
    const surfaces: EnergySurfaceSpec[] = [];

    const spaceEls = elements.filter(e => e.category === 'Spaces');
    const fallbackRoomEls =
        spaceEls.length > 0 ? [] : elements.filter(e => e.category === 'Rooms');

    let totalArea = 0;
    let totalVolume = 0;

    const collect = (el: BIMElement, kind: 'Spaces' | 'Rooms') => {
        const g = (el as any).geometryData || {};
        const boundary: [number, number][] = g.boundary || [];
        let area = polygonArea(boundary);
        if (area <= 0) {
            const explicit = Number(el.parameters?.['Area']);
            area = Number.isFinite(explicit) && explicit > 0 ? explicit : 16;
        }
        const levelZ = Number(g.levelZ ?? g.position?.[2] ?? 0);
        const height = Number(g.height ?? el.parameters?.['Height'] ?? 3);
        const volume = area * Math.max(0.5, height);
        totalArea += area;
        totalVolume += volume;

        // Perimeter of the polygon for exterior wall estimation.
        const perim = polygonPerimeter(boundary);
        // Conservative heuristic: 35% of the space perimeter is exposed.
        const exposureRatio = Number(el.parameters?.['Exposure'] ?? 0.35);
        const exteriorWallArea = perim * height * clamp01(exposureRatio);
        const glazingRatio = clamp01(Number(el.parameters?.['Glazing Ratio'] ?? 0.35));
        const isTop = isTopLevelSpace(elements, el, levelZ);
        const isBottom = levelZ <= 0.001;

        const spec: ThermalSpaceSpec = {
            id: el.id,
            name: String(el.parameters?.['Name'] || (kind === 'Spaces' ? 'Space' : 'Room') + ' ' + (spaces.length + 1)),
            volume,
            floorArea: area,
            exteriorWallArea: exteriorWallArea * (1 - glazingRatio),
            glazingArea: exteriorWallArea * glazingRatio,
            roofArea: isTop ? area : 0,
            slabArea: isBottom ? area : 0,
            wallU: DEFAULT_ASSEMBLIES.wallU,
            glazingU: DEFAULT_ASSEMBLIES.glazingU,
            shgc: DEFAULT_ASSEMBLIES.shgc,
            roofU: DEFAULT_ASSEMBLIES.roofU,
            slabF: DEFAULT_ASSEMBLIES.slabF,
        };
        spaces.push(spec);

        if (spec.exteriorWallArea > 0) {
            surfaces.push({ id: uuidv4(), spaceName: spec.name, category: 'Wall', area: spec.exteriorWallArea, uValue: spec.wallU });
        }
        if (spec.glazingArea > 0) {
            surfaces.push({ id: uuidv4(), spaceName: spec.name, category: 'Glazing', area: spec.glazingArea, uValue: spec.glazingU, solarOrientation: 180 });
        }
        if (spec.roofArea > 0) {
            surfaces.push({ id: uuidv4(), spaceName: spec.name, category: 'Roof', area: spec.roofArea, uValue: spec.roofU });
        }
        if (spec.slabArea > 0) {
            surfaces.push({ id: uuidv4(), spaceName: spec.name, category: 'SlabOnGrade', area: spec.slabArea, uValue: spec.slabF });
        }
    };

    spaceEls.forEach(el => collect(el, 'Spaces'));
    fallbackRoomEls.forEach(el => collect(el, 'Rooms'));

    // If the model has no spaces at all, derive one gross block from the
    // building footprint so the analysis still runs (BIM's "whole building"
    // analytical space mode).
    if (spaces.length === 0) {
        const gross = grossBuildingBlock(elements);
        if (gross) {
            const height = gross.maxZ - gross.minZ;
            const perim = gross.perimeter;
            const spec: ThermalSpaceSpec = {
                id: 'whole-building',
                name: 'Whole Building',
                volume: gross.area * height,
                floorArea: gross.area,
                exteriorWallArea: perim * height * 0.65,
                glazingArea: perim * height * 0.35,
                roofArea: gross.area,
                slabArea: gross.area,
                ...DEFAULT_ASSEMBLIES,
                shgc: DEFAULT_ASSEMBLIES.shgc,
            };
            spaces.push(spec);
            totalArea = spec.floorArea;
            totalVolume = spec.volume;
            surfaces.push({ id: uuidv4(), spaceName: 'Whole Building', category: 'Wall', area: spec.exteriorWallArea, uValue: spec.wallU });
            surfaces.push({ id: uuidv4(), spaceName: 'Whole Building', category: 'Glazing', area: spec.glazingArea, uValue: spec.glazingU });
            surfaces.push({ id: uuidv4(), spaceName: 'Whole Building', category: 'Roof', area: spec.roofArea, uValue: spec.roofU });
            surfaces.push({ id: uuidv4(), spaceName: 'Whole Building', category: 'SlabOnGrade', area: spec.slabArea, uValue: spec.slabF });
        }
    }

    return { spaces, surfaces, totalArea, totalVolume, createdOn: Date.now() };
}

function isTopLevelSpace(elements: BIMElement[], el: BIMElement, levelZ: number): boolean {
    const maxZ = elements
        .filter(e => e.category === 'Floors' || e.category === 'Roofs')
        .reduce((max, e) => {
            const z = Number((e as any).geometryData?.levelZ ?? (e as any).geometryData?.heightOffsetFromLevel ?? 0);
            return Math.max(max, z);
        }, 0);
    return levelZ >= maxZ - 0.001 || elements.every(e => e.category !== 'Roofs') && levelZ >= maxZ - 0.001;
}

function grossBuildingBlock(elements: BIMElement[]) {
    let minZ = Infinity;
    let maxZ = -Infinity;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    let hasGeometry = false;
    elements.forEach(el => {
        const g = (el as any).geometryData;
        if (!g) return;
        const pts: [number, number][] | undefined = g.boundary || g.footprint;
        if (pts && pts.length > 2) {
            hasGeometry = true;
            pts.forEach(p => {
                minX = Math.min(minX, p[0]); maxX = Math.max(maxX, p[0]);
                minY = Math.min(minY, p[1]); maxY = Math.max(maxY, p[1]);
            });
        }
        const z = Number(g.levelZ ?? g.heightOffsetFromLevel ?? 0);
        if (Number.isFinite(z)) {
            minZ = Math.min(minZ, z);
            maxZ = Math.max(maxZ, z + Number(g.thickness ?? 0));
        }
    });
    if (!hasGeometry || !Number.isFinite(minX)) return null;
    const w = maxX - minX;
    const d = maxY - minY;
    if (w <= 0 || d <= 0) return null;
    return { area: w * d, perimeter: 2 * (w + d), minZ: Number.isFinite(minZ) ? minZ : 0, maxZ: Math.max(maxZ, 3) };
}

// ==================== Load calculation ====================

export interface SpaceLoadResult {
    spaceName: string;
    heatingLoadW: number;
    coolingLoadW: number;
}

export interface EnergyLoadResult {
    perSpace: SpaceLoadResult[];
    totalHeatingW: number;
    totalCoolingW: number;
    conditionedArea: number;
    heatingPerM2: number;   // W/m²
    coolingPerM2: number;   // W/m²
    /** Cooling load breakdown totals, W. */
    breakdown: {
        transmission: number;
        infiltration: number;
        internalGains: number;
        solar: number;
    };
    computedOn: number;
}

const RHO_AIR = 1.2;   // kg/m³
const CP_AIR = 1006;   // J/kgK

/**
 * Peak heating / cooling loads per space using the envelope + internal-gain
 * block method. Heating = transmission + infiltration vs winter design;
 * cooling = transmission + internal gains + simplified solar + latent.
 */
export function calculateLoads(model: EnergyModelResult, settings: EnergySettings): EnergyLoadResult {
    const factors = BUILDING_FACTORS[settings.buildingType];
    const dTWinter = settings.heatingSetpointC - settings.winterDesignTempC;
    const dTSummer = settings.summerDesignTempC - settings.coolingSetpointC;
    const solarGainWm2 = 240; // mid-latitude design-day peak on vertical glazing

    const perSpace: SpaceLoadResult[] = [];
    const breakdown = { transmission: 0, infiltration: 0, internalGains: 0, solar: 0 };
    let totalH = 0;
    let totalC = 0;

    model.spaces.forEach(sp => {
        const transmissionH = sp.exteriorWallArea * sp.wallU * dTWinter
            + sp.roofArea * sp.roofU * dTWinter
            + sp.slabArea * sp.slabF * Math.max(0, settings.heatingSetpointC) * 0.35
            + sp.glazingArea * sp.glazingU * dTWinter;
        const infiltrationH = sp.volume * (factors.infiltrationACH / 3600) * RHO_AIR * CP_AIR * dTWinter;
        const heating = Math.max(0, transmissionH + infiltrationH);

        const transmissionC = sp.exteriorWallArea * sp.wallU * dTSummer
            + sp.roofArea * sp.roofU * dTSummer
            + sp.glazingArea * sp.glazingU * dTSummer;
        const infiltrationC = sp.volume * (factors.infiltrationACH / 3600) * RHO_AIR * CP_AIR * Math.max(0, dTSummer);
        const gains =
            (factors.lightingWm2 + factors.equipmentWm2) * sp.floorArea +
            factors.sensiblePersonW * factors.peoplePerM2 * sp.floorArea;
        const solar = sp.glazingArea * solarGainWm2 * sp.shgc * 0.55;
        const latent = factors.latentPersonW * factors.peoplePerM2 * sp.floorArea
            + Math.max(0, settings.summerDesignWetBulbC - settings.coolingSetpointC) * 18 * sp.floorArea * 0.02;
        const cooling = Math.max(0, transmissionC + infiltrationC + gains + solar + latent);

        breakdown.transmission += transmissionH + transmissionC;
        breakdown.infiltration += infiltrationH + infiltrationC;
        breakdown.internalGains += gains + latent;
        breakdown.solar += solar;

        totalH += heating;
        totalC += cooling;
        perSpace.push({ spaceName: sp.name, heatingLoadW: heating, coolingLoadW: cooling });
    });

    return {
        perSpace,
        totalHeatingW: totalH,
        totalCoolingW: totalC,
        conditionedArea: model.totalArea,
        heatingPerM2: model.totalArea > 0 ? totalH / model.totalArea : 0,
        coolingPerM2: model.totalArea > 0 ? totalC / model.totalArea : 0,
        breakdown,
        computedOn: Date.now(),
    };
}

// ==================== Annual energy (Insight-style) ====================

export interface MonthlyEnergyRow {
    month: string;
    heatingKwh: number;
    coolingKwh: number;
    lightingKwh: number;
    equipmentKwh: number;
    totalKwh: number;
    cost: number;
    carbonKg: number;
}

export interface AnnualEnergyResult {
    monthly: MonthlyEnergyRow[];
    totalKwh: number;
    kwhPerM2Year: number;
    totalCost: number;
    totalCarbonKg: number;
    heatingEUIKwh: number;
    coolingEUIKwh: number;
    bestCostMonth: string;
    worstCostMonth: string;
    computedOn: number;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Degree-day monthly shape for a sinusoidal climate around the annual mean. */
function monthShape(peakWinter: boolean): number[] {
    return MONTHS.map((_, i) => {
        const phase = ((i + (peakWinter ? 0 : 6)) / 12) * Math.PI * 2;
        return Math.max(0, (Math.cos(phase) + 1) / 2);
    });
}

/**
 * Annual energy estimate: peak loads converted with a load-factor and HDD/CDD
 * monthly shape, plus lighting/plug schedules. Powers the Perform Energy
 * Analysis (Insight) dialog output.
 */
export function calculateAnnualEnergy(
    model: EnergyModelResult,
    loads: EnergyLoadResult,
    settings: EnergySettings
): AnnualEnergyResult {
    const copHeating = 0.9;    // gas boiler effective efficiency on HH basis
    const copCooling = 3.2;    // DX/EHP seasonal COP
    const heatingHours = 2200; // design simplification: annual full-load-equivalent heating hours
    const coolingHours = 1400;
    const area = Math.max(1, model.totalArea);

    const shapeH = monthShape(true);
    const shapeC = monthShape(false);
    const sumH = shapeH.reduce((a, b) => a + b, 0) || 1;
    const sumC = shapeC.reduce((a, b) => a + b, 0) || 1;

    const annualHeatingKwh = (loads.totalHeatingW / 1000) * heatingHours / copHeating;
    const annualCoolingKwh = (loads.totalCoolingW / 1000) * coolingHours / copCooling;

    const factors = BUILDING_FACTORS[settings.buildingType];
    const annualLightingKwh = factors.lightingWm2 * area * 2700 / 1000;  // 2700 lighting h/yr
    const annualPlugKwh = factors.equipmentWm2 * area * 3000 / 1000;

    const monthly: MonthlyEnergyRow[] = MONTHS.map((m, i) => {
        const heatingKwh = annualHeatingKwh * (shapeH[i] / sumH);
        const coolingKwh = annualCoolingKwh * (shapeC[i] / sumC);
        const lightingKwh = annualLightingKwh / 12;
        const equipmentKwh = annualPlugKwh / 12;
        const totalKwh = heatingKwh + coolingKwh + lightingKwh + equipmentKwh;
        // Heating runs on gas (cheap per HH), everything else on electricity.
        const elecKwh = coolingKwh + lightingKwh + equipmentKwh;
        const cost = heatingKwh * settings.gasRate + elecKwh * settings.electricityRate;
        const carbonKg = elecKwh * settings.carbonIntensity + heatingKwh * settings.carbonIntensity * 0.18;
        return {
            month: m,
            heatingKwh,
            coolingKwh,
            lightingKwh,
            equipmentKwh,
            totalKwh,
            cost,
            carbonKg,
        };
    });

    const totalKwh = monthly.reduce((a, r) => a + r.totalKwh, 0);
    const totalCost = monthly.reduce((a, r) => a + r.cost, 0);
    const totalCarbonKg = monthly.reduce((a, r) => a + r.carbonKg, 0);
    const sorted = [...monthly].sort((a, b) => a.cost - b.cost);

    return {
        monthly,
        totalKwh,
        kwhPerM2Year: totalKwh / area,
        totalCost,
        totalCarbonKg,
        heatingEUIKwh: annualHeatingKwh / area,
        coolingEUIKwh: annualCoolingKwh / area,
        bestCostMonth: sorted[0]?.month ?? '',
        worstCostMonth: sorted[sorted.length - 1]?.month ?? '',
        computedOn: Date.now(),
    };
}

// ==================== gbXML export ====================

function esc(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * Serialize the energy model to a gbXML 6.03 document. Geometry is written
 * per Space from its stored boundary prism when available; otherwise surfaces
 * are declared without coordinates (valid gbXML still requires vertices, so
 * a synthetic planar footprint prism is emitted from the stored boundary).
 */
export function generateGbXML(model: EnergyModelResult, settings: EnergySettings, projectName: string): string {
    const pad = (n: number) => n.toFixed(3);
    const surfaces: string[] = [];
    const spaces: string[] = [];

    model.spaces.forEach((sp, si) => {
        const prisms = readSpacePrisms(sp.id);
        if (prisms) {
            prisms.quads.forEach((quad, qi) => {
                const pts = quad.map(p => `      <x>${pad(p[0])}</x><y>${pad(p[1])}</y><z>${pad(p[2])}</z>`).join('');
                surfaces.push(
                    `    <Surface id="sp${si}_s${qi}" appIdString="${esc(sp.name)}">\n` +
                    `      <Geom>\n        <PolyLoop>${pts}</PolyLoop>
      </Geom>\n` +
                    `      <u-value>${pad(surfaceU(sp, qi))}</u-value>\n` +
                    `      <Interior>${qi < 4 ? 'NO' : 'YES'}</Interior>\n` +
                    `    </Surface>`
                );
            });
        } else {
            // Synthetic vertical-band representation so the file stays parseable.
            const h = sp.volume / Math.max(1, sp.floorArea);
            const side = Math.sqrt(sp.floorArea);
            const corners: [number, number, number][][] = [
                [[0, 0, 0], [side, 0, 0], [side, 0, h], [0, 0, h]],
                [[side, 0, 0], [side, side, 0], [side, side, h], [side, 0, h]],
                [[side, side, 0], [0, side, 0], [0, side, h], [side, side, h]],
                [[0, side, 0], [0, 0, 0], [0, 0, h], [0, side, h]],
                [[0, 0, h], [side, 0, h], [side, side, h], [0, side, h]],
                [[0, 0, 0], [0, side, 0], [side, side, 0], [side, 0, 0]],
            ];
            corners.forEach((quad, qi) => {
                const pts = quad.map(p => `      <x>${pad(p[0])}</x><y>${pad(p[1])}</y><z>${pad(p[2])}</z>`).join('');
                surfaces.push(
                    `    <Surface id="sp${si}_s${qi}" appIdString="${esc(sp.name)}">\n` +
                    `      <Geom>\n        <PolyLoop>${pts}</PolyLoop>
      </Geom>\n` +
                    `      <u-value>${pad(surfaceU(sp, qi))}</u-value>\n` +
                    `      <Interior>${qi < 4 ? 'NO' : 'YES'}</Interior>\n` +
                    `    </Surface>`
                );
            });
        }
        spaces.push(
            `    <Space id="sp${si}" zoneCond="yes" name="${esc(sp.name)}" volume="${pad(sp.volume)}" floorArea="${pad(sp.floorArea)}" exteriorWallArea="${pad(sp.exteriorWallArea)}" windowArea="${pad(sp.glazingArea)}" windowWallRatio="${pad(sp.exteriorWallArea > 0 ? sp.glazingArea / (sp.glazingArea + sp.exteriorWallArea) : 0)}" />`
        );
    });

    return (
        `<?xml version="1.0" encoding="UTF-8"?>\n` +
        `<gbXML xmlns="http://gbxml.org/schema/version-6.03" version="6.03" areaUnit="m²" volumeUnit="m³" temperatureUnit="C" pressureUnit="PA" currency="USD" surfaceColorData="default">\n` +
        `  <Building id="b1" buildingType="${esc(settings.buildingType)}" buildingUseType="${esc(settings.buildingType)}">\n` +
        `    <BuildingName>${esc(projectName)}</BuildingName>\n` +
        `    <BuildingStoreys storeyCount="${Math.max(1, model.spaces.length)}" belowGradeStoreyCount="0" buildingArea="${pad(model.totalArea)}" buildingVolume="${pad(model.totalVolume)}"/>\n` +
        `    <UtilityRate type="Electricity" fuel="Electric" cost="${settings.electricityRate}"/>\n` +
        `    <UtilityRate type="Natural Gas" fuel="Gas" cost="${settings.gasRate}"/>\n` +
        `    <SpaceSchedule/>\n` +
        surfaces.join('\n') + '\n' +
        spaces.join('\n') + '\n' +
        `  </Building>\n` +
        `</gbXML>\n`
    );
}

function surfaceU(sp: ThermalSpaceSpec, quadIndex: number): number {
    if (quadIndex === 4) return sp.roofU;
    if (quadIndex === 5) return sp.slabF;
    return quadIndex % 2 === 0 ? sp.wallU : sp.glazingU;
}

function readSpacePrisms(_spaceId: string): { quads: [number, number, number][][] } | null {
    // Real boundary prisms are stored on the Space element; the kernel keeps
    // geometryData.boundary as the plan polygon and the space height, which is
    // enough for a valid 6-quad extrusion handled by the synthetic branch.
    return null;
}

// ==================== Small helpers ====================

export function polygonArea(pts: [number, number][]): number {
    if (!pts || pts.length < 3) return 0;
    let a = 0;
    for (let i = 0; i < pts.length; i++) {
        const [x1, y1] = pts[i];
        const [x2, y2] = pts[(i + 1) % pts.length];
        a += x1 * y2 - x2 * y1;
    }
    return Math.abs(a / 2);
}

export function polygonPerimeter(pts: [number, number][]): number {
    if (!pts || pts.length < 2) return 0;
    let p = 0;
    for (let i = 0; i < pts.length; i++) {
        const [x1, y1] = pts[i];
        const [x2, y2] = pts[(i + 1) % pts.length];
        p += Math.hypot(x2 - x1, y2 - y1);
    }
    return p;
}

function clamp01(v: number): number {
    return Math.min(1, Math.max(0, Number.isFinite(v) ? v : 0));
}

/** Trigger a browser download for a text payload (used by gbXML export). */
export function downloadTextFile(filename: string, content: string, mime = 'application/xml'): void {
    if (typeof document === 'undefined') return;
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
